import React, { createRef, useEffect, useRef, useState } from "react";
import Img from "gatsby-image";
import FocusTrap from "focus-trap-react";
import Observer from "@researchgate/react-intersection-observer";
import useNoBodyScroll from "../../hooks/useNoBodyScroll";
import MenuCloseIcon from "../../assets/MenuCloseIcon";
import Arrow from "../../assets/Arrow";
import cssClass from "./ClinicPhotoGallery.module.scss";

const ClinicPhotoGallery = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  useNoBodyScroll(lightboxIsOpen);

  // Array of references to each image
  const imageRefs = useRef([]);
  if (imageRefs.current.length !== images.length) {
    imageRefs.current = Array(images.length)
      .fill()
      .map((_, i) => imageRefs.current[i] || createRef());
  }

  const changeImage = (delta) => {
    if (currentImageIndex === 0) {
      setCurrentImageIndex(images.length - 1);
    } else if (currentImageIndex === images.length - 1) {
      setCurrentImageIndex(0);
    } else {
      setCurrentImageIndex(currentImageIndex + delta);
    }
  };

  useEffect(() => {
    if (lightboxIsOpen) {
      imageRefs.current[currentImageIndex].current.scrollIntoView();
    }
  }, [currentImageIndex, lightboxIsOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIsOpen) {
        if (e.key === "Escape") setLightboxIsOpen(false);
        if (e.key === "ArrowLeft") changeImage(-1); // FIXME: these only update once, but still log events?
        if (e.key === "ArrowRight") changeImage(+1); // FIXME: these only update once, but still log events?
      }
    };

    if (lightboxIsOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIsOpen]);

  return (
    <>
      <div
        className={cssClass.container}
        role="button"
        tabIndex={0}
        onClick={() => setLightboxIsOpen(true)}
        onKeyDown={() => setLightboxIsOpen(true)}
      >
        <Img
          fluid={images[currentImageIndex].fluid}
          alt={images[currentImageIndex].title}
        />
        {images.length > 1 && (
          <div className={cssClass.indexIndicator}>
            <span>
              {currentImageIndex + 1} / {images.length}
            </span>
          </div>
        )}
      </div>

      {lightboxIsOpen && (
        <FocusTrap>
          <div className={cssClass.lightboxContainer}>
            <div className={cssClass.lightboxHeader}>
              <span>
                {currentImageIndex + 1} of {images.length}
              </span>
              <button
                className={cssClass.closeButton}
                onClick={() => setLightboxIsOpen(false)}
              >
                <MenuCloseIcon color="var(--color-off-black)" />
              </button>
            </div>

            <button
              className={cssClass.leftButton}
              onClick={() => changeImage(-1)}
            >
              <Arrow color="var(--color-off-black)" />
            </button>

            <div className={cssClass.lightboxCarousel}>
              {images.map((image, i) => (
                <div
                  ref={imageRefs.current[i]}
                  key={image.title}
                  className={cssClass.lightboxImageContainer}
                >
                  <Observer
                    onChange={({ isIntersecting }) => {
                      if (isIntersecting) setCurrentImageIndex(i);
                    }}
                  >
                    <Img
                      className={cssClass.lightboxImage}
                      fluid={image.fluid}
                      alt={image.title}
                    />
                  </Observer>
                </div>
              ))}
            </div>

            <button
              className={cssClass.rightButton}
              onClick={() => changeImage(+1)}
            >
              <Arrow color="var(--color-off-black)" />
            </button>
          </div>
        </FocusTrap>
      )}
    </>
  );
};

export default ClinicPhotoGallery;
